/**
 * ConsultationPage Component
 * Dedicated full-page medical consultation interface
 *
 * Replaces the modal-based ConsultationDialog for better UX:
 * - Persists across hot reloads (data loaded from backend via URL params)
 * - Allows navigating to other tabs without losing the consultation
 * - Full-screen 2-column layout with sidebar for patient info
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Favorite as HeartIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  LocalHospital as MedicalIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  MedicalRecord,
  ConsultationQueueItem,
  UpdateMedicalRecordData,
  CompleteConsultationData,
  MedicalRecordStatus,
  MedicalOpinion,
  getMedicalOpinionLabel,
  getMedicalOpinionColor,
  formatBloodPressure,
} from '../../types/medicalRecord';
import medicalRecordService from '../../services/medicalRecordService';
import { getApiErrorMessage } from '../../types/common';
import { useAuth } from '../../stores/authStore';
import { PageShell, HelpButton, ActionButton, PrerequisiteBlock, type PrerequisiteBlockProps } from '../../components';
import { prerequisiteFromError } from '../../utils/prerequisiteFromError';
import { AnamnesisForm } from './components/AnamnesisForm';
import { AnamneseDoTrabalhadorPanel } from './components/AnamneseDoTrabalhadorPanel';
import { PhysicalExamForm } from './components/PhysicalExamForm';
import { ConclusionForm } from './components/ConclusionForm';
import { TriageView } from './components/TriageView';
import { openSignedFile } from '../../services/fileAccess';
import { ExamsTab } from './components/ExamsTab';
import type { AppointmentExam } from '../../types/appointmentExam';
import {
  APPOINTMENT_EXAM_STATUS_LABELS,
  APPOINTMENT_EXAM_STATUS_COLORS,
  APPLICATION_LEVEL_LABELS,
  APPLICATION_LEVEL_COLORS,
} from '../../types/appointmentExam';
import type { ConsultationMode } from '../../types/appointmentExam';
import appointmentExamService from '../../services/appointmentExamService';
import appointmentService from '../../services/appointmentService';

// ============================================================================
// TAB PANEL
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`consultation-tabpanel-${index}`}
      aria-labelledby={`consultation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ConsultationPage: React.FC = () => {
  const { appointmentId: appointmentIdParam } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const appointmentId = Number(appointmentIdParam);

  // Guard against StrictMode double-invocation creating duplicate records
  const loadingRef = useRef(false);

  // Loading state
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingAso, setGeneratingAso] = useState(false);
  const [generatingDocs, setGeneratingDocs] = useState(false);
  const [generatingProntuario, setGeneratingProntuario] = useState(false);

  // Data state
  const [queueItem, setQueueItem] = useState<ConsultationQueueItem | null>(null);
  const [medicalRecord, setMedicalRecord] = useState<MedicalRecord | null>(null);
  const [prerequisiteBlock, setPrerequisiteBlock] = useState<PrerequisiteBlockProps | null>(null);

  // Appointment exams state
  const [appointmentExams, setAppointmentExams] = useState<AppointmentExam[]>([]);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Form data state
  const [anamnesisData, setAnamnesisData] = useState<UpdateMedicalRecordData>({});
  const [physicalExamData, setPhysicalExamData] = useState<UpdateMedicalRecordData>({});
  const [conclusionData, setConclusionData] = useState<CompleteConsultationData>({
    medicalOpinion: MedicalOpinion.APT,
  });

  // Derived state
  const isEditable = medicalRecord?.status === MedicalRecordStatus.IN_PROGRESS;
  const isCompleted = medicalRecord?.status === MedicalRecordStatus.COMPLETED;

  // Consultation mode: FULL or EXAM_ONLY
  const consultationMode: ConsultationMode = useMemo(() => {
    if (!appointmentExams.length) return 'FULL'; // no exams = standard behavior
    const hasClinic = appointmentExams.some(
      (ae) => ae.occupationalExam?.clinicalConsultation,
    );
    const examType =
      queueItem?.examType || medicalRecord?.appointment?.examType || '';
    if (hasClinic || examType === 'Consulta Clínica') return 'FULL';
    return 'EXAM_ONLY';
  }, [appointmentExams, queueItem, medicalRecord]);

  // Initialize form data from medical record
  const initializeFormData = useCallback((record: MedicalRecord) => {
    setAnamnesisData({
      chiefComplaint: record.chiefComplaint,
      occupationalHistory: record.occupationalHistory,
      currentJobDescription: record.currentJobDescription,
      previousJobs: record.previousJobs,
      workAccidentHistory: record.workAccidentHistory,
      medicalHistory: record.medicalHistory,
      familyHistory: record.familyHistory,
      currentMedications: record.currentMedications,
      allergies: record.allergies,
      smokingStatus: record.smokingStatus,
      smokingDetails: record.smokingDetails,
      alcoholUse: record.alcoholUse,
      alcoholDetails: record.alcoholDetails,
      physicalActivity: record.physicalActivity,
      physicalActivityDetails: record.physicalActivityDetails,
    });

    setPhysicalExamData({
      generalCondition: record.generalCondition,
      skinExam: record.skinExam,
      headNeckExam: record.headNeckExam,
      cardiovascularExam: record.cardiovascularExam,
      respiratoryExam: record.respiratoryExam,
      abdominalExam: record.abdominalExam,
      neurologicalExam: record.neurologicalExam,
      musculoskeletalExam: record.musculoskeletalExam,
      otherFindings: record.otherFindings,
    });

    if (record.medicalOpinion) {
      setConclusionData({
        medicalOpinion: record.medicalOpinion,
        restrictions: record.restrictions,
        recommendations: record.recommendations,
        returnDate: record.returnDate,
        cidCodes: record.cidCodes ? JSON.parse(record.cidCodes) : null,
      });
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    if (!appointmentId || isNaN(appointmentId)) {
      enqueueSnackbar('ID de agendamento inválido', { variant: 'error' });
      navigate('/medical-consultations');
      return;
    }

    if (loadingRef.current) return;
    loadingRef.current = true;

    const loadData = async () => {
      setPageLoading(true);
      try {
        // Load queue item for patient info and triage data
        const queueResult = await medicalRecordService.getConsultationQueue({
          attendanceStatus: ['CHECKED_IN', 'TRIAGED', 'IN_PROGRESS', 'COMPLETED'],
          limit: 1000,
        });
        const item = queueResult.data.find((q) => q.id === appointmentId);
        if (item) {
          setQueueItem(item);
        }

        // Load appointment exams
        try {
          const exams = await appointmentExamService.getExamsByAppointment(appointmentId);
          setAppointmentExams(exams);
        } catch (examsError) {
          console.error('Error loading appointment exams:', examsError);
          // Non-blocking: consultation can continue without exams
        }

        // Load medical record by appointment
        const record = await medicalRecordService.getMedicalRecordByAppointment(appointmentId);
        if (record) {
          setMedicalRecord(record);
          initializeFormData(record);
        } else if (user?.id) {
          // No record exists — start consultation automatically
          // Use queue item or fallback to appointment data for employeeId
          let employeeId = item?.employeeId;
          if (!employeeId) {
            try {
              const appointment = await appointmentService.getAppointmentById(appointmentId);
              employeeId = appointment?.employeeId;
            } catch {
              console.error('Could not load appointment for employeeId');
            }
          }
          if (employeeId) {
            const newRecord = await medicalRecordService.startConsultation({
              appointmentId,
              employeeId,
              professionalId: Number(user.id),
            });
            if (newRecord) {
              setMedicalRecord(newRecord);
              initializeFormData(newRecord);
              enqueueSnackbar('Consulta iniciada com sucesso', { variant: 'success' });
            }
          }
        }
      } catch (error) {
        console.error('Error loading consultation data:', error);
        const block = prerequisiteFromError(error);
        if (block) {
          setPrerequisiteBlock(block);
        } else {
          enqueueSnackbar('Erro ao carregar dados da consulta', { variant: 'error' });
        }
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
    // Mount-once effect: loadingRef prevents re-execution even if deps change
  }, [appointmentId, enqueueSnackbar, navigate, user, initializeFormData]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!medicalRecord?.id || !isEditable) return;

    setSaving(true);
    try {
      const combinedData = {
        ...anamnesisData,
        ...physicalExamData,
      };
      const updated = await medicalRecordService.updateMedicalRecord(
        medicalRecord.id,
        combinedData
      );
      if (updated) {
        setMedicalRecord(updated);
        enqueueSnackbar('Dados salvos com sucesso', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error saving:', error);
      enqueueSnackbar('Erro ao salvar dados', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [medicalRecord, isEditable, anamnesisData, physicalExamData, enqueueSnackbar]);

  // Generate ASO handler
  const handleGenerateAso = useCallback(async () => {
    if (!medicalRecord?.id || !isCompleted) return;

    const target = window.open('about:blank', '_blank');
    setGeneratingAso(true);
    try {
      const result = await medicalRecordService.generateAsoPdf(medicalRecord.id);
      if (result?.pdfPath) {
        if (target) {
          try {
            await openSignedFile(result.pdfPath, target);
          } catch (err) {
            target.close();
            enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir ASO', { variant: 'error' });
          }
        }
        // Reload record to get updated asoPdfPath
        const updated = await medicalRecordService.getMedicalRecordByAppointment(appointmentId);
        if (updated) setMedicalRecord(updated);
      } else if (target) {
        target.close();
      }
      enqueueSnackbar('ASO gerado com sucesso', { variant: 'success' });
    } catch (error) {
      if (target) target.close();
      console.error('Error generating ASO:', error);
      const errorMessage = getApiErrorMessage(error, 'Erro ao gerar ASO');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setGeneratingAso(false);
    }
  }, [medicalRecord, isCompleted, appointmentId, enqueueSnackbar]);

  // Complete and print handler (conclude + generate ASO + Prontuario)
  const handleCompleteAndPrint = useCallback(async () => {
    if (!medicalRecord?.id || !isEditable) return;

    setGeneratingDocs(true);
    try {
      // 1. Save form data
      const combinedData = { ...anamnesisData, ...physicalExamData };
      await medicalRecordService.updateMedicalRecord(medicalRecord.id, combinedData);

      // 2. Complete consultation
      const completed = await medicalRecordService.completeConsultation(
        medicalRecord.id,
        conclusionData
      );
      if (!completed) throw new Error('Falha ao concluir');
      setMedicalRecord(completed);
      initializeFormData(completed);

      // 3. Generate ASO + Prontuario in parallel
      const [asoResult, prontuarioResult] = await Promise.all([
        medicalRecordService.generateAsoPdf(completed.id),
        medicalRecordService.generateProntuarioPdf(completed.id),
      ]);

      // 4. Open PDFs for printing (windows opened before awaits to satisfy popup blocker)
      const openPdfs = async () => {
        if (asoResult?.pdfPath) {
          const asoTarget = window.open('about:blank', '_blank');
          if (asoTarget) {
            try {
              await openSignedFile(asoResult.pdfPath, asoTarget);
            } catch (err) {
              asoTarget.close();
              enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir ASO', { variant: 'error' });
            }
          }
        }
        if (prontuarioResult?.pdfPath) {
          const prontuarioTarget = window.open('about:blank', '_blank');
          if (prontuarioTarget) {
            try {
              await openSignedFile(prontuarioResult.pdfPath, prontuarioTarget);
            } catch (err) {
              prontuarioTarget.close();
              enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir Prontuário', { variant: 'error' });
            }
          }
        }
      };
      await openPdfs();

      // 5. Reload record to update asoPdfPath
      const updated = await medicalRecordService.getMedicalRecordByAppointment(appointmentId);
      if (updated) setMedicalRecord(updated);

      enqueueSnackbar('Consulta concluída e documentos gerados', { variant: 'success' });
    } catch (error) {
      console.error('Error in complete and print:', error);
      const errorMessage = getApiErrorMessage(error, 'Erro ao concluir e gerar documentos');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setGeneratingDocs(false);
    }
  }, [
    medicalRecord,
    isEditable,
    anamnesisData,
    physicalExamData,
    conclusionData,
    initializeFormData,
    appointmentId,
    enqueueSnackbar,
  ]);

  // Generate Prontuario handler
  const handleGenerateProntuario = useCallback(async () => {
    if (!medicalRecord?.id || !isCompleted) return;

    const target = window.open('about:blank', '_blank');
    setGeneratingProntuario(true);
    try {
      const result = await medicalRecordService.generateProntuarioPdf(medicalRecord.id);
      if (result?.pdfPath) {
        if (target) {
          try {
            await openSignedFile(result.pdfPath, target);
          } catch (err) {
            target.close();
            enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir Prontuário', { variant: 'error' });
          }
        }
      } else if (target) {
        target.close();
      }
      enqueueSnackbar('Prontuário gerado com sucesso', { variant: 'success' });
    } catch (error) {
      console.error('Error generating Prontuario:', error);
      const errorMessage = getApiErrorMessage(error, 'Erro ao gerar prontuário');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setGeneratingProntuario(false);
    }
  }, [medicalRecord, isCompleted, enqueueSnackbar]);

  // Get triage data from queue item or medical record associations
  const triageData =
    queueItem?.triageRecords?.[0] ||
    medicalRecord?.appointment?.triageRecords?.[0] ||
    null;

  // Patient name for breadcrumb
  const patientName =
    queueItem?.employee?.name || medicalRecord?.employee?.name || 'Paciente';

  // ============================================================================
  // RENDER
  // ============================================================================

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Consultas Médicas', path: '/medical-consultations' },
    { label: patientName },
  ];

  if (pageLoading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Consulta Médica">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (prerequisiteBlock) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Consulta Médica">
        <PrerequisiteBlock {...prerequisiteBlock} />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Consulta Médica"
      actions={
        <>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate('/medical-consultations')}
              variant="outlined"
              size="small"
            >
              Voltar
            </Button>
            <HelpButton entity="consultation" screen="atendimento" size="small" />
            {isEditable && (
              <>
                <ActionButton
                  acao="medical-records.update"
                  rotulo="Salvar"
                  variant="outlined"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || generatingDocs}
                >
                  Salvar
                </ActionButton>
                <ActionButton
                  acao="medical-records.complete"
                  rotulo="Concluir e Imprimir"
                  variant="contained"
                  color="success"
                  startIcon={
                    generatingDocs ? <CircularProgress size={16} /> : <PrintIcon />
                  }
                  onClick={handleCompleteAndPrint}
                  disabled={generatingDocs || !conclusionData.medicalOpinion}
                >
                  {generatingDocs ? 'Gerando documentos...' : 'Concluir e Imprimir'}
                </ActionButton>
              </>
            )}

            {isCompleted && (
              <>
                {medicalRecord?.asoPdfPath ? (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PdfIcon />}
                    onClick={async () => {
                      if (!medicalRecord.asoPdfPath) return;
                      const target = window.open('about:blank', '_blank');
                      if (!target) return;
                      try {
                        await openSignedFile(medicalRecord.asoPdfPath, target);
                      } catch (err) {
                        target.close();
                        enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir ASO', { variant: 'error' });
                      }
                    }}
                  >
                    Visualizar ASO
                  </Button>
                ) : (
                  <ActionButton
                    acao="medical-records.generate-aso"
                    rotulo="Gerar ASO"
                    variant="contained"
                    color="secondary"
                    startIcon={
                      generatingAso ? <CircularProgress size={16} /> : <PdfIcon />
                    }
                    onClick={handleGenerateAso}
                    disabled={generatingAso}
                  >
                    Gerar ASO
                  </ActionButton>
                )}
                <ActionButton
                  acao="medical-records.generate-prontuario"
                  rotulo="Gerar Prontuário"
                  variant="outlined"
                  color="primary"
                  startIcon={
                    generatingProntuario ? <CircularProgress size={16} /> : <PdfIcon />
                  }
                  onClick={handleGenerateProntuario}
                  disabled={generatingProntuario}
                >
                  Gerar Prontuário
                </ActionButton>
              </>
            )}
        </>
      }
    >
        {/* Status chips */}
        {medicalRecord && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Chip
              label={isCompleted ? 'Concluído' : 'Em Andamento'}
              color={isCompleted ? 'success' : 'info'}
              size="small"
            />
            {isCompleted && medicalRecord.medicalOpinion && (
              <Chip
                label={getMedicalOpinionLabel(medicalRecord.medicalOpinion)}
                color={getMedicalOpinionColor(medicalRecord.medicalOpinion)}
                size="small"
              />
            )}
          </Box>
        )}

        {/* Main Content - 2 columns */}
        <Grid container spacing={3}>
          {/* Left: Tabs with forms (8 cols) */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {consultationMode === 'FULL' ? (
                  <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab
                      icon={<HistoryIcon />}
                      iconPosition="start"
                      label="Anamnese"
                      id="consultation-tab-0"
                    />
                    <Tab
                      icon={<AssignmentIcon />}
                      iconPosition="start"
                      label="Exame Físico"
                      id="consultation-tab-1"
                    />
                    {appointmentExams.length > 0 && (
                      <Tab
                        icon={<ScienceIcon />}
                        iconPosition="start"
                        label="Exames Complementares"
                        id="consultation-tab-2"
                      />
                    )}
                    <Tab
                      icon={<HeartIcon />}
                      iconPosition="start"
                      label="Triagem"
                      id={`consultation-tab-${appointmentExams.length > 0 ? 3 : 2}`}
                    />
                    <Tab
                      icon={<MedicalIcon />}
                      iconPosition="start"
                      label="Conclusão/ASO"
                      id={`consultation-tab-${appointmentExams.length > 0 ? 4 : 3}`}
                    />
                  </Tabs>
                ) : (
                  <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab
                      icon={<ScienceIcon />}
                      iconPosition="start"
                      label="Exames Complementares"
                      id="consultation-tab-0"
                    />
                    <Tab
                      icon={<MedicalIcon />}
                      iconPosition="start"
                      label="Conclusão Simplificada"
                      id="consultation-tab-1"
                    />
                  </Tabs>
                )}
              </Box>

              <Box sx={{ p: 3 }}>
                {consultationMode === 'FULL' ? (
                  <>
                    <TabPanel value={activeTab} index={0}>
                      <AnamneseDoTrabalhadorPanel
                        appointmentId={appointmentId}
                        data={anamnesisData}
                        onChange={setAnamnesisData}
                        disabled={!isEditable}
                      />
                      <AnamnesisForm
                        data={anamnesisData}
                        onChange={setAnamnesisData}
                        disabled={!isEditable}
                      />
                    </TabPanel>
                    <TabPanel value={activeTab} index={1}>
                      <PhysicalExamForm
                        data={physicalExamData}
                        onChange={setPhysicalExamData}
                        disabled={!isEditable}
                      />
                    </TabPanel>
                    {appointmentExams.length > 0 && (
                      <TabPanel value={activeTab} index={2}>
                        <ExamsTab
                          appointmentId={appointmentId}
                          appointmentExams={appointmentExams}
                          onExamsUpdate={setAppointmentExams}
                        />
                      </TabPanel>
                    )}
                    <TabPanel value={activeTab} index={appointmentExams.length > 0 ? 3 : 2}>
                      <TriageView triageData={triageData} />
                    </TabPanel>
                    <TabPanel value={activeTab} index={appointmentExams.length > 0 ? 4 : 3}>
                      <ConclusionForm
                        data={conclusionData}
                        onChange={setConclusionData}
                        disabled={!isEditable}
                        isCompleted={isCompleted}
                        appointmentExams={appointmentExams}
                      />
                      {isCompleted && (
                        <Box sx={{ mt: 2 }}>
                          <Alert severity="success">
                            Consulta concluída em{' '}
                            {medicalRecord?.consultationCompletedAt
                              ? new Date(
                                  medicalRecord.consultationCompletedAt
                                ).toLocaleString('pt-BR')
                              : '-'}
                          </Alert>
                        </Box>
                      )}
                    </TabPanel>
                  </>
                ) : (
                  <>
                    <TabPanel value={activeTab} index={0}>
                      <ExamsTab
                        appointmentId={appointmentId}
                        appointmentExams={appointmentExams}
                        onExamsUpdate={setAppointmentExams}
                      />
                    </TabPanel>
                    <TabPanel value={activeTab} index={1}>
                      <ConclusionForm
                        data={conclusionData}
                        onChange={setConclusionData}
                        disabled={!isEditable}
                        isCompleted={isCompleted}
                        appointmentExams={appointmentExams}
                      />
                      {isCompleted && (
                        <Box sx={{ mt: 2 }}>
                          <Alert severity="success">
                            Consulta concluída em{' '}
                            {medicalRecord?.consultationCompletedAt
                              ? new Date(
                                  medicalRecord.consultationCompletedAt
                                ).toLocaleString('pt-BR')
                              : '-'}
                          </Alert>
                        </Box>
                      )}
                    </TabPanel>
                  </>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Right: Sidebar (4 cols) */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Patient Data */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Dados do Paciente
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Nome:</strong>{' '}
                  {queueItem?.employee?.name ||
                    medicalRecord?.employee?.name ||
                    'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>CPF:</strong>{' '}
                  {queueItem?.employee?.cpf ||
                    medicalRecord?.employee?.cpf ||
                    'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Empresa:</strong>{' '}
                  {queueItem?.employee?.company?.tradeName ||
                    queueItem?.employee?.company?.legalName ||
                    medicalRecord?.employee?.company?.tradeName ||
                    medicalRecord?.employee?.company?.legalName ||
                    'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Tipo de Exame:</strong>{' '}
                  {queueItem?.examType ||
                    medicalRecord?.appointment?.examType ||
                    'N/A'}
                </Typography>
                {(queueItem?.queueNumber ||
                  medicalRecord?.appointment?.queueNumber) && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Senha:</strong>{' '}
                    <Chip
                      label={
                        queueItem?.queueNumber ||
                        medicalRecord?.appointment?.queueNumber
                      }
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Vital Signs */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <HeartIcon fontSize="small" color="error" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Sinais Vitais (Triagem)
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                {triageData ? (
                  <Grid container spacing={1}>
                    <Grid size={6}>
                      <Typography variant="body2">
                        <strong>PA:</strong>{' '}
                        {formatBloodPressure(
                          triageData.bloodPressureSystolic,
                          triageData.bloodPressureDiastolic
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2">
                        <strong>FC:</strong>{' '}
                        {triageData.heartRate
                          ? `${triageData.heartRate} bpm`
                          : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2">
                        <strong>Temp:</strong>{' '}
                        {triageData.temperature
                          ? `${triageData.temperature} °C`
                          : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2">
                        <strong>SpO2:</strong>{' '}
                        {triageData.oxygenSaturation
                          ? `${triageData.oxygenSaturation}%`
                          : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2">
                        <strong>Peso:</strong>{' '}
                        {triageData.weight ? `${triageData.weight} kg` : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2">
                        <strong>Altura:</strong>{' '}
                        {triageData.height ? `${triageData.height} cm` : '-'}
                      </Typography>
                    </Grid>
                    {triageData.bmi && (
                      <Grid size={12}>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>IMC:</strong>{' '}
                          {Number(triageData.bmi).toFixed(1)} kg/m²
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Triagem não realizada
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Appointment Exams sidebar */}
            {appointmentExams.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ScienceIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Exames ({appointmentExams.length})
                    </Typography>
                    {consultationMode === 'EXAM_ONLY' && (
                      <Chip label="EXAM_ONLY" size="small" color="info" variant="outlined" />
                    )}
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  {appointmentExams.map((exam) => (
                    <Box key={exam.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={APPOINTMENT_EXAM_STATUS_LABELS[exam.status]}
                        color={APPOINTMENT_EXAM_STATUS_COLORS[exam.status]}
                        size="small"
                        sx={{ minWidth: 85 }}
                      />
                      <Typography variant="body2" noWrap>
                        {exam.occupationalExam?.examName ?? `Exame #${exam.occupationalExamId}`}
                      </Typography>
                      {exam.applicationLevel && (
                        <Chip
                          label={APPLICATION_LEVEL_LABELS[exam.applicationLevel] ?? exam.applicationLevel}
                          color={APPLICATION_LEVEL_COLORS[exam.applicationLevel] ?? 'default'}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 18 }}
                        />
                      )}
                      {exam.periodicityMonths && (
                        <Typography variant="caption" color="text.secondary">
                          {exam.periodicityMonths}m
                        </Typography>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
    </PageShell>
  );
};

export default ConsultationPage;
